import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { request } from "app/network";

interface AddPointUsePolicyModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    darkMode?: boolean;
    editPolicy?: any; // Policy to edit (null for add mode)
}

interface AddPointUsePolicyModalState {
    policyName: string;
    useUnit: number;
    minimumUseAmount: number;
    maximumUseAmount: string;
    isActive: boolean;
    isDefault: boolean;
    loading: boolean;
    errors: { [key: string]: string };
}

class AddPointUsePolicyModal extends Component<AddPointUsePolicyModalProps, AddPointUsePolicyModalState> {
    constructor(props: AddPointUsePolicyModalProps) {
        super(props);
        this.state = {
            policyName: "",
            useUnit: 100,
            minimumUseAmount: 100,
            maximumUseAmount: "",
            isActive: true,
            isDefault: false,
            loading: false,
            errors: {},
        };
    }

    componentDidUpdate(prevProps: AddPointUsePolicyModalProps) {
        // When modal opens with editPolicy, populate the form
        if (this.props.open && !prevProps.open && this.props.editPolicy) {
            this.setState({
                policyName: this.props.editPolicy.policyName || "",
                useUnit: this.props.editPolicy.useUnit || 100,
                minimumUseAmount: this.props.editPolicy.minimumUseAmount || 100,
                maximumUseAmount: this.props.editPolicy.maximumUseAmount ? String(this.props.editPolicy.maximumUseAmount) : "",
                isActive: this.props.editPolicy.isActive !== undefined ? this.props.editPolicy.isActive : true,
                isDefault: this.props.editPolicy.isDefault !== undefined ? this.props.editPolicy.isDefault : false,
                errors: {},
            });
        } else if (this.props.open && !prevProps.open && !this.props.editPolicy) {
            // Reset form for add mode
            this.setState({
                policyName: "",
                useUnit: 100,
                minimumUseAmount: 100,
                maximumUseAmount: "",
                isActive: true,
                isDefault: false,
                errors: {},
            });
        }
    }

    handleChange = (field: string) => (event: any) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        this.setState({ [field]: value, errors: { ...this.state.errors, [field]: "" } } as any);
    };

    validateForm = () => {
        const errors: { [key: string]: string } = {};
        const { policyName, useUnit, minimumUseAmount, maximumUseAmount } = this.state;

        if (!policyName.trim()) errors.policyName = "정책명을 입력해주세요.";
        if (useUnit <= 0) errors.useUnit = "사용 단위는 0보다 커야 합니다.";
        if (minimumUseAmount <= 0) errors.minimumUseAmount = "최소 사용 금액은 0보다 커야 합니다.";
        if (maximumUseAmount && parseInt(maximumUseAmount) <= 0) {
            errors.maximumUseAmount = "최대 사용 금액은 0보다 커야 합니다.";
        }
        if (maximumUseAmount && parseInt(maximumUseAmount) < minimumUseAmount) {
            errors.maximumUseAmount = "최대 사용 금액은 최소 사용 금액보다 크거나 같아야 합니다.";
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    };

    handleSubmit = async () => {
        if (!this.validateForm()) return;

        this.setState({ loading: true });

        try {
            const param = {
                PolicyName: this.state.policyName,
                UseUnit: this.state.useUnit,
                MinimumUseAmount: this.state.minimumUseAmount,
                MaximumUseAmount: this.state.maximumUseAmount ? parseInt(this.state.maximumUseAmount) : null,
                IsActive: this.state.isActive,
                IsDefault: this.state.isDefault,
            };

            if (this.props.editPolicy) {
                // Edit mode
                await request("PointUsePolicy.update", {
                    pathParams: { id: this.props.editPolicy.seqId },
                    paramz: param
                });
            } else {
                // Add mode
                await request("PointUsePolicy.create", { paramz: param });
            }

            this.setState({
                policyName: "",
                useUnit: 100,
                minimumUseAmount: 100,
                maximumUseAmount: "",
                isActive: true,
                isDefault: false,
                loading: false,
                errors: {},
            });

            this.props.onSuccess();
            this.props.onClose();
        } catch (error: any) {
            console.error("포인트 사용 정책 저장 실패:", error);
            this.setState({ loading: false });
        }
    };

    render() {
        const { open, onClose, darkMode } = this.props;
        const { loading, errors } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? '#1a2035' : '#ffffff',
                        backgroundImage: 'none',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        color: darkMode ? '#ffffff' : 'inherit',
                        fontWeight: 'medium',
                        fontSize: '1.25rem',
                    }}
                >
                    {this.props.editPolicy ? "포인트 사용 정책 수정" : "포인트 사용 정책 추가"}
                </DialogTitle>
                <DialogContent>
                    <MDBox pt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="정책명 *"
                                    value={this.state.policyName}
                                    onChange={this.handleChange("policyName")}
                                    error={!!errors.policyName}
                                    helperText={errors.policyName}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="사용 단위 *"
                                    value={this.state.useUnit}
                                    onChange={this.handleChange("useUnit")}
                                    error={!!errors.useUnit}
                                    helperText={errors.useUnit || "예: 100 (100포인트 단위로 사용)"}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
                                        },
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="최소 사용 금액 *"
                                    value={this.state.minimumUseAmount}
                                    onChange={this.handleChange("minimumUseAmount")}
                                    error={!!errors.minimumUseAmount}
                                    helperText={errors.minimumUseAmount}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="최대 사용 금액"
                                    value={this.state.maximumUseAmount}
                                    onChange={this.handleChange("maximumUseAmount")}
                                    error={!!errors.maximumUseAmount}
                                    helperText={errors.maximumUseAmount || "선택사항 (미입력 시 제한 없음)"}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiInputBase-input': {
                                            color: darkMode ? '#ffffff' : 'inherit',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : 'inherit',
                                            },
                                        },
                                        '& .MuiFormHelperText-root': {
                                            color: darkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
                                        },
                                        '& input[type=number]': {
                                            colorScheme: darkMode ? 'dark' : 'normal',
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.isActive}
                                            onChange={this.handleChange("isActive")}
                                            disabled={loading}
                                        />
                                    }
                                    label="활성화"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={this.state.isDefault}
                                            onChange={this.handleChange("isDefault")}
                                            disabled={loading}
                                        />
                                    }
                                    label="기본 정책"
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="secondary" disabled={loading}>
                        취소
                    </MDButton>
                    <MDButton onClick={this.handleSubmit} color="info" disabled={loading}>
                        {loading ? (this.props.editPolicy ? "수정 중..." : "생성 중...") : (this.props.editPolicy ? "수정" : "추가")}
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default AddPointUsePolicyModal;
